"use client"

import SmallArrowSVG from "images/Icons/SmallArrow.inline.svg"
import type { UniversalLinkProps } from "library/link"
import UniversalLink from "library/link"
import { css, fresponsive, styled } from "library/styled"
import type { ReactNode } from "react"
import colors from "styles/colors"
import textStyles from "styles/text"

const colorLibrary = {
	light: {
		text: colors.green400,
		hover: {
			text: colors.green200,
		},
	},
	lightAlt: {
		text: colors.green200,
		hover: {
			text: colors.green400,
		},
	},
	dark: {
		text: colors.greenAccent,
		hover: {
			text: colors.white,
		},
	},
	alt: {
		text: colors.green300,
		hover: {
			text: colors.green200,
		},
	},
}

export default function LinkButton({
	children,
	size = "large",
	variant = "light",
	...props
}: UniversalLinkProps & {
	children: ReactNode
	size?: "small" | "medium" | "large" | "alt" | "xl"
	variant?: "light" | "lightAlt" | "dark" | "alt"
}) {
	return (
		<Wrapper {...props} variant={variant} size={size}>
			<TextContainer>{children}</TextContainer>
			<SmallWrapper className="arrow-wrapper" size={size}>
				<div className="small-arrow-main">
					<SmallArrow size={size} />
				</div>
				<div className="small-arrow-alt">
					<SmallArrow size={size} />
				</div>
			</SmallWrapper>
		</Wrapper>
	)
}

const Wrapper = styled(
	UniversalLink,
	({
		variant = "light",
		size = "large",
	}: {
		variant?: "light" | "lightAlt" | "dark" | "alt"
		size?: "small" | "medium" | "large" | "alt" | "xl"
	}) => {
		const linkColors = colorLibrary[variant]

		return {
			...fresponsive(css`
				position: relative;
				display: block;
				cursor: pointer;
				align-items: center;
				width: fit-content;
				padding-right: 12px;
				color: ${linkColors.text};
				transition: color 0.3s cubic-bezier(0.215, 0.61, 0.355, 1);

				${size === "xl"
					? css`
							${textStyles.custom.bodyL};
							padding-right: 16px;
						`
					: size === "large"
						? css`
								${textStyles.custom.bodyR};
								padding-right: 12px;
							`
						: size === "medium"
							? css`
									${textStyles.custom.bodyS};
									padding-right: 10px;
								`
							: size === "small"
								? css`
										${textStyles.custom.bodyXS};
										padding-right: 8px;
									`
								: css`
										${textStyles.titleS};
										padding-right: 6px;
									`}

				.small-arrow-main {
					transform: translateY(0);
					opacity: 1;
					transition:
						transform 0.3s cubic-bezier(0.215, 0.61, 0.355, 1),
						opacity 0.3s cubic-bezier(0.215, 0.61, 0.355, 1);
				}

				.small-arrow-alt {
					position: absolute;
					top: 0;
					left: 0;
					transform: translateY(10px);
					opacity: 0;
					transition:
						transform 0.3s cubic-bezier(0.215, 0.61, 0.355, 1),
						opacity 0.3s cubic-bezier(0.215, 0.61, 0.355, 1);
				}

				&:hover {
					color: ${linkColors.hover.text};
				}

				&:hover .small-arrow-main {
					transform: translateY(-10px);
					opacity: 0;
				}

				&:hover .small-arrow-alt {
					transform: translateY(0);
					opacity: 1;
				}
			`),
		}
	},
)

const SmallArrow = styled(
	SmallArrowSVG,
	({
		size = "large",
	}: {
		size?: "small" | "medium" | "large" | "alt" | "xl"
	}) => {
		const arrowSizes = {
			small: "6px",
			medium: "8px",
			large: "10px",
			alt: "6px",
			xl: "12px",
		}

		return {
			...fresponsive(css`
				width: ${arrowSizes[size]};
				height: ${arrowSizes[size]};
				position: absolute;
				top: 0;
				left: 0;
			`),
		}
	},
)

const SmallWrapper = styled(
	"div",
	({
		size = "large",
	}: {
		size?: "small" | "medium" | "large" | "alt" | "xl"
	}) => {
		const arrowSizes = {
			alt: "6px",
			small: "6px",
			medium: "8px",
			large: "10px",
			xl: "12px",
		}

		return {
			...fresponsive(css`
				width: ${arrowSizes[size]};
				height: 10px;
				right: 0;
				top: 50%;
				transform: translateY(-50%);
				position: absolute;
				overflow: hidden;
			`),
		}
	},
)

const TextContainer = styled("div", {
	...fresponsive(css`
		display: flex;
	`),
})
